import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { CREDENTIALS } from "@/data";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] } },
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};

const headshot = "https://skyagent-artifacts.skywork.ai/router/agent/2026-05-18/prod_agent_a846de8d-e74f-4935-a1af-f907b9b64fb4/Bharadvajasana%20II%20-%20Yogaroot_8077_fb26a38f23e642c6a090aa4d59352e64.jpg";
const groupPhoto = "https://skyagent-artifacts.skywork.ai/router/agent/2026-05-18/prod_agent_a846de8d-e74f-4935-a1af-f907b9b64fb4/DSC06757_97afb4fd52b84e308167177a8aae87b0.jpg";

export default function About() {
  return (
    <div className="bg-[#fdfbf7] overflow-x-hidden">
      {/* ── HERO ─────────────────────────────────────── */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${groupPhoto})` }} />
        <div className="absolute inset-0 bg-gradient-to-br from-[#2c1f0e]/90 via-[#2c1f0e]/80 to-[#1a1208]/90" />
        <div className="relative z-10 container mx-auto px-6 text-center text-white pt-28">
          <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-3xl mx-auto">
            <motion.p variants={fadeUp} className="text-[#c8a96e] text-xs tracking-[0.4em] uppercase mb-4">
              關於創辦人
            </motion.p>
            <motion.h1 variants={fadeUp} className="font-serif text-5xl md:text-6xl text-[#fdfbf7] mb-6">
              Virginia Law
            </motion.h1>
            <motion.p variants={fadeUp} className="text-[#f0e6d3]/80 text-lg leading-relaxed">
              E-RYT 500 · RPYT · YACEP · Abhyaas - School of Yoga & Wellness™ 創辦人
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* ── STORY: 2002 ──────────────────────────────── */}
      <section className="py-24 bg-[#fdfbf7]">
        <div className="container mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="max-w-6xl mx-auto grid md:grid-cols-2 gap-16 items-center"
          >
            <motion.div variants={fadeUp} className="relative">
              <div className="relative rounded-3xl overflow-hidden aspect-[3/4] max-w-sm mx-auto shadow-2xl">
                <img src={headshot} alt="Virginia Law" className="w-full h-full object-cover object-top" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#2c1f0e]/60 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="text-[#fdfbf7] font-serif text-xl">Virginia Law</div>
                  <div className="text-[#c8a96e] text-xs tracking-widest uppercase mt-1">創辦人 · 首席導師</div>
                </div>
              </div>
              <div className="absolute -top-8 -left-8 w-40 h-40 rounded-full bg-[#c8a96e]/10 blur-3xl pointer-events-none" />
            </motion.div>

            <motion.div variants={fadeUp}>
              <p className="text-[#c8a96e] text-xs tracking-[0.3em] uppercase mb-4">起點</p>
              <h2 className="font-serif text-3xl md:text-4xl text-[#2c1f0e] mb-6 leading-tight">
                瑜伽，是我尋找自我的旅程，<br />
                <span className="text-[#c8a96e]">也是我最真實的選擇。</span>
              </h2>
              <div className="space-y-4 text-[#5c4033]/80 leading-relaxed">
                <p>
                  2002年，我踏上瑜伽墊，不是為了「健身」——而是因為我渴望看見內心真實的樣子，尋找情緒的平穩與內心的安宜。就是這一步，改變了我的一生。
                </p>
                <p>
                  往後六年，我全身投入這條道路——在印度的道場深修、取得國際認證、在香港開始全職教學。 2010年，我創辦了 Abhyaas，取自梅文「持續的練習」。
                </p>
                <p>
                  Abhyaas 不只是一個瑜伽學校——它是一個信念的體現：<strong className="text-[#2c1f0e]">不論你從哪裡開始，只要持續練習，改變就會發生。</strong>
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── QUOTE DIVIDER ─────────────────────────────── */}
      <section className="bg-[#2c1f0e] py-16">
        <div className="container mx-auto px-6 text-center max-w-3xl">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <div className="text-[#c8a96e] text-5xl font-serif mb-6 leading-none">"</div>
            <p className="font-serif text-2xl md:text-3xl text-[#f0e6d3] italic leading-relaxed mb-6">
              這不是由上而下的傳授，<br />
              而是我們並肩同行的旅程。
            </p>
            <p className="text-[#c8a96e]/70 text-sm tracking-widest">— Virginia Law</p>
          </motion.div>
        </div>
      </section>

      {/* ── JOURNEY TIMELINE ─────────────────────────── */}
      <section className="py-24 bg-[#f5f0e8]">
        <div className="container mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="max-w-4xl mx-auto"
          >
            <motion.p variants={fadeUp} className="text-[#c8a96e] text-xs tracking-[0.3em] uppercase mb-4 text-center">
              修行之路
            </motion.p>
            <motion.h2 variants={fadeUp} className="font-serif text-3xl md:text-4xl text-[#2c1f0e] text-center mb-16">
              走過的每一步，都是教材
            </motion.h2>

            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-[#c8a96e]/30" />
              {[
                { year: "2002", title: "遇見瑜伽", desc: "尋找內心真實的樣子，追求情緒的平穩與內心的安宜，踏上了瑜伽的旅程。" },
                { year: "2008", title: "踏上全職教學之路", desc: "離開所有其他工作，全職投身瑜伽教學。同年在印度國際喜馬拉雅瑜伽奧林匹克獲得亞軍。" },
                { year: "2010", title: "創辦 Abhyaas", desc: "在旺角彌敦道創立 Abhyaas Yoga & Wellness，意為「持續的練習」。" },
                { year: "2015", title: "開辦 RYT200 師資培訓", desc: "正式開始 Yoga Alliance 認可的師資培訓課程，至今已完成37屆，培育300+位畢業導師。" },
                { year: "2019", title: "RPYT 產前瑜伽", desc: "取得 Yoga Alliance RPYT 認證，開始培訓產前瑜伽導師。" },
                { year: "2022", title: "HKU SPACE Lead Trainer", desc: "擔任香港大學 SPACE RYT200 課程首席導師 (Lead Trainer)，將師資培訓帶入高等教育平台。" },
                { year: "2026", title: "知識王國啟航", desc: "數位化轉型，建立 Abhyaas 知識王國，讓智慧穿越地域界限，在線上持續傳播。" },
              ].map((item, i) => (
                <motion.div
                  key={item.year}
                  variants={fadeUp}
                  className={`relative flex items-start gap-8 mb-12 ${i % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"}`}
                >
                  <div className={`flex-1 ${i % 2 === 0 ? "md:text-right md:pr-12" : "md:pl-12"} pl-12 md:pl-0`}>
                    <div className="text-[#c8a96e] text-xs tracking-widest uppercase mb-1">{item.year}</div>
                    <h3 className="font-serif text-xl text-[#2c1f0e] mb-2">{item.title}</h3>
                    <p className="text-[#5c4033]/70 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                  {/* Dot */}
                  <div className="absolute left-0 md:left-1/2 md:-translate-x-1/2 w-8 h-8 bg-[#2c1f0e] rounded-full border-2 border-[#c8a96e] flex items-center justify-center flex-shrink-0 mt-1 z-10">
                    <div className="w-2 h-2 rounded-full bg-[#c8a96e]" />
                  </div>
                  <div className="flex-1 hidden md:block" />
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── CREDENTIALS ─────────────────────────────── */}
      <section className="py-20 bg-[#fdfbf7]">
        <div className="container mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="max-w-4xl mx-auto"
          >
            <motion.p variants={fadeUp} className="text-[#c8a96e] text-xs tracking-[0.3em] uppercase mb-4 text-center">
              認證與資歷
            </motion.p>
            <motion.h2 variants={fadeUp} className="font-serif text-3xl text-[#2c1f0e] text-center mb-12">
              專業認證
            </motion.h2>

            <motion.div variants={stagger} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { cert: "E-RYT 500", body: "Yoga Alliance", desc: "最高級別登記瑜伽導師" },
                { cert: "RPYT", body: "Yoga Alliance", desc: "登記產前瑜伽導師" },
                { cert: "YACEP", body: "Yoga Alliance", desc: "認可持續教育提供者" },
                { cert: "RYT200 Lead Trainer", body: "HKU SPACE", desc: "香港大學持續進修學院首席導師" },
                { cert: "Sivananda Yoga 深造認證", body: "印度 Sivananda Vedanta Centre", desc: "國際認可傳統瑜伽培訓" },
                { cert: "喜馬拉雅瑜伽奧林匹克亞軍", body: "2008 年印度國際賽事", desc: "國際競賽認可成就" },
              ].map((c) => (
                <motion.div
                  key={c.cert}
                  variants={fadeUp}
                  className="flex items-start gap-4 bg-white p-6 rounded-2xl border border-[#c8a96e]/15 shadow-sm"
                  style={{ boxShadow: "0 2px 12px -2px rgba(44,31,14,0.06)" }}
                >
                  <div className="w-10 h-10 rounded-full bg-[#2c1f0e] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <div className="w-3 h-3 rounded-full bg-[#c8a96e]" />
                  </div>
                  <div>
                    <div className="font-serif text-lg text-[#2c1f0e] mb-0.5">{c.cert}</div>
                    <div className="text-[#c8a96e] text-xs tracking-wide mb-1">{c.body}</div>
                    <div className="text-[#5c4033]/60 text-sm">{c.desc}</div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── PHILOSOPHY ─────────────────────────────── */}
      <section className="py-20 bg-[#2c1f0e]">
        <div className="container mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="max-w-4xl mx-auto"
          >
            <motion.p variants={fadeUp} className="text-[#c8a96e] text-xs tracking-[0.3em] uppercase mb-4 text-center">
              教學理念
            </motion.p>
            <motion.h2 variants={fadeUp} className="font-serif text-3xl md:text-4xl text-[#fdfbf7] text-center mb-14">
              這不是X，而是Y
            </motion.h2>

            <motion.div variants={stagger} className="grid md:grid-cols-2 gap-6">
              {[
                { x: "這不是拉筋健身", y: "而是讓身體重新呼吸" },
                { x: "這不是大師傳授", y: "而是同路人一起成長" },
                { x: "這不是完美的體式", y: "而是誠實的自我覺察" },
                { x: "這不是課程畢業", y: "而是修行旅程的開始" },
              ].map((p) => (
                <motion.div
                  key={p.x}
                  variants={fadeUp}
                  className="bg-[#3d2b1a]/60 rounded-2xl p-6 border border-[#c8a96e]/15"
                >
                  <div className="text-[#f0e6d3]/50 text-sm line-through mb-2">{p.x}</div>
                  <div className="text-[#c8a96e] font-serif text-lg">{p.y}</div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── BOOK ─────────────────────────────────────── */}
      <section className="py-20 bg-[#f5f0e8]">
        <div className="container mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="max-w-4xl mx-auto text-center"
          >
            <motion.p variants={fadeUp} className="text-[#c8a96e] text-xs tracking-[0.3em] uppercase mb-4">著作</motion.p>
            <motion.h2 variants={fadeUp} className="font-serif text-3xl md:text-4xl text-[#2c1f0e] mb-6">
              《生活即修行：情緒急救包》
            </motion.h2>
            <motion.p variants={fadeUp} className="text-[#5c4033]/80 leading-relaxed max-w-2xl mx-auto mb-4">
              53個章節，五大情緒主題——憤怒與焦慮、悲傷與孤獨、自我批評、關係、身體。這是我多年教學與修行的結晶，也是送給每一位走在情緒旅途上的人的陪伴。
            </motion.p>
            <motion.p variants={fadeUp} className="text-[#8a7060] text-sm mb-8">速熊文化 出版</motion.p>
            <motion.a
              variants={fadeUp}
              href="#contact"
              className="inline-flex items-center gap-2 bg-[#2c1f0e] text-[#c8a96e] px-8 py-3.5 rounded-full font-semibold hover:bg-[#3d2b1a] transition-colors"
            >
              查詢購書 <ArrowRight size={16} />
            </motion.a>
          </motion.div>
        </div>
      </section>

      {/* ── CTA ─────────────────────────────────────── */}
      <section className="py-20 bg-gradient-to-br from-[#2c1f0e] to-[#1a1208]">
        <div className="container mx-auto px-6 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="max-w-2xl mx-auto">
            <motion.h2 variants={fadeUp} className="font-serif text-3xl text-[#fdfbf7] mb-6">
              與 Virginia 一起修行
            </motion.h2>
            <motion.p variants={fadeUp} className="text-[#f0e6d3]/70 mb-8">
              無論你是剛開始，還是已走了很長的路——每一個當下，都是最好的起點。
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-wrap justify-center gap-4">
              <a href="/courses" className="inline-flex items-center gap-2 bg-[#c8a96e] text-[#2c1f0e] px-8 py-4 rounded-full font-bold hover:bg-[#d4b87c] transition-colors">
                查看課程 <ArrowRight size={18} />
              </a>
              <a href="mailto:abhyaas.hk@gmail.com" className="inline-flex items-center gap-2 border border-[#c8a96e]/60 text-[#c8a96e] px-8 py-4 rounded-full font-semibold hover:bg-[#c8a96e]/10 transition-colors">
                電郵查詢
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
